import {SubmitHandler} from 'react-hook-form';
import {NextPage} from 'next';
import {useRouter} from 'next/router';

import {Screen} from '#component/base';
import {ResetPasswordForm} from '#component/form';
import {AuthCTAMessages} from '#component/partial';
import {EventType, useAnalytics, UserActionEvent} from '#component/provider/AnalyticsProvider';
import {useAuth} from '#hook';
import {useReturnUrl} from '#hook';
import {useToastQueue} from '#hook';
import {sprinkles} from '#style/sprinkles';

export const ResetPassword: NextPage = () => {
  const {query} = useRouter();
  const {addToast} = useToastQueue();
  const {resetPassword} = useAuth();
  const {targetResolver} = useReturnUrl();
  const {token, return_to: returnUrl = '/signin'} = query;
  const {track} = useAnalytics();

  const handleSubmit: SubmitHandler<{password: string}> = async ({password}) => {
    const resetSuccess = await resetPassword({
      newPassword: password,
      token: token as string,
      returnUrl: returnUrl as string,
      resolveTarget: targetResolver(returnUrl as string),
    });

    if (resetSuccess) {
      addToast({children: 'Password reset! Please proceed to log in.'});
      track<UserActionEvent>({evtName: EventType.UserAction, actionName: 'Reset Password Success'});
    }
  };

  return (
    <Screen
      contentContainerClassName={sprinkles({
        flex: 'flex',
        content: ['end', 'end', 'center'],
        dir: 'col',
      })}
      // Because this page also handles redirection to return URL, we should
      // not set the `userState` prop to disable user state checks. This way
      // we can avoid potential conflicts and complications.
      pageName="Reset Password"
      sidePanelImageUrl="/image/reset-password.jpg">
      <div
        className={sprinkles({
          direction: 'col',
          flex: 'flex',
          height: 'full',
          placeContent: ['end', 'center'],
        })}>
        <AuthCTAMessages
          title="Reset Password?"
          messages={['Password must be 8 characters or longer.']}
        />

        <ResetPasswordForm onSubmit={handleSubmit} />
      </div>
    </Screen>
  );
};
